import { useCallback } from 'react';
import {
  BanIcon,
  BellIcon,
  Calendar,
  Check,
  ClockIcon,
  DocumentIcon,
  Euro,
  InformationCircle,
  LinkIcon,
  Mail,
  NoteIcon,
  RefreshIcon,
  SendIcon,
  TrashIcon,
  Warning,
  WarningCircle,
} from 'assets/icons';
import Button from 'components/button';
import UseContext from 'hooks/useContext';
import {
  authorMention,
  formatEventAt,
  groupEventsByMonth,
  historyEventTexts,
} from './invoiceHistoryText';
import useInvoiceHistory from './useInvoiceHistory';

const NEUTRAL = {
  Icon: InformationCircle,
  color: '#4B5563',
  box: 'bg-gray-100',
};

// The icon of each type, in a square tinted like the mockup (4.1).
const EVENT_ICONS = {
  fetched: { Icon: DocumentIcon, color: '#4B5563', box: 'bg-gray-100' },
  imported: { Icon: Check, color: '#15803D', box: 'bg-green-50' },
  import_failed: { Icon: WarningCircle, color: '#B91C1C', box: 'bg-red-50' },
  pennylane_category_set: {
    Icon: NoteIcon,
    color: '#4B5563',
    box: 'bg-gray-100',
  },
  category_changed: { Icon: NoteIcon, color: '#4B5563', box: 'bg-gray-100' },
  payment_term_changed: {
    Icon: Calendar,
    color: '#4B5563',
    box: 'bg-gray-100',
  },
  payment_status_changed: { Icon: Euro, color: '#4B5563', box: 'bg-gray-100' },
  appendix_added: { Icon: LinkIcon, color: '#4B5563', box: 'bg-gray-100' },
  appendix_sent: { Icon: SendIcon, color: '#15803D', box: 'bg-green-50' },
  appendix_deleted: { Icon: TrashIcon, color: '#4B5563', box: 'bg-gray-100' },
  sent_by_mail: { Icon: Mail, color: '#4B5563', box: 'bg-gray-100' },
  reminder_sent: { Icon: BellIcon, color: '#B45309', box: 'bg-yellow-50' },
  litigation_opened: { Icon: Warning, color: '#B91C1C', box: 'bg-red-50' },
  litigation_closed: { Icon: Check, color: '#4B5563', box: 'bg-gray-100' },
  reminders_suspended: { Icon: BanIcon, color: '#4B5563', box: 'bg-gray-100' },
  reminders_resumed: { Icon: RefreshIcon, color: '#15803D', box: 'bg-green-50' },
};

const iconOf = (type) =>
  Object.prototype.hasOwnProperty.call(EVENT_ICONS, type)
    ? EVENT_ICONS[type]
    : NEUTRAL;

const EventRow = ({ icon, label, details = [], footnote }) => {
  const { Icon, color, box } = icon;
  return (
    <li className="flex gap-3 rounded-lg border border-gray-100 px-3 py-3">
      <div
        aria-hidden
        className={`flex flex-shrink-0 items-center justify-center w-9 h-9 rounded-md ${box}`}
      >
        <Icon wh={18} color={color} />
      </div>
      <div className="flex flex-col min-w-0">
        <div className="text-sm font-medium text-gray-900">{label}</div>
        {details.map((line) => (
          <div key={line} className="text-sm text-gray-700">
            {line}
          </div>
        ))}
        {footnote && <div className="text-sm text-gray-500">{footnote}</div>}
      </div>
    </li>
  );
};

const MonthGroup = ({ label, children }) => (
  <section className="rounded-lg border border-gray-100 p-2">
    <h4 className="px-2 py-2 text-sm font-medium text-gray-900">{label}</h4>
    <ul className="flex flex-col gap-2">{children}</ul>
  </section>
);

/**
 * The "Historique" tab of an invoice's detail (JBWEB-1278, mockup 4.1): its
 * events by month, newest first. The history starts with 1278, without any
 * catch-up (api README, decision 43): a migrated invoice says that what came
 * before is not known.
 */
const InvoiceHistoryTab = ({ invoice, categories, refreshKey }) => {
  const { language } = UseContext();
  const text = useCallback(
    (key, fallback) => language.getValue(`invoiceHistory.${key}`, fallback),
    [language],
  );
  const texts = {
    text,
    journalText: (key, fallback) =>
      language.getValue(`invoiceJournal.${key}`, fallback),
    listText: (key, fallback) =>
      language.getValue(`invoiceList.${key}`, fallback),
  };
  const history = useInvoiceHistory(invoice.number, refreshKey);

  if (history.failure) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-sm text-gray-700"
      >
        <div>
          {history.failure === 'notFound'
            ? text(
                'notFound',
                'Cette facture est introuvable : elle n’a pas d’historique.',
              )
            : text('loadFailed', 'L’historique n’a pas pu être chargé.')}
        </div>
        {history.failure === 'failed' && (
          <Button color="SECONDARY" onClick={history.reload}>
            {text('retryLoad', 'Réessayer')}
          </Button>
        )}
      </div>
    );
  }
  if (!history.events) {
    return (
      <div role="status" className="py-16 text-center text-sm text-gray-500">
        {text('loading', 'Chargement de l’historique…')}
      </div>
    );
  }

  const isMigrated = invoice.origin === 'migrated';
  if (history.events.length === 0 && !isMigrated) {
    return (
      <div className="py-16 text-center text-sm text-gray-500">
        {text('empty', 'Aucun évènement')}
      </div>
    );
  }

  const groups = groupEventsByMonth(text, history.events);
  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <MonthGroup key={group.key} label={group.label}>
          {group.events.map((event) => {
            const { label, details } = historyEventTexts(texts, event, {
              categories,
              language,
            });
            const footnote = [
              formatEventAt(text, event.at),
              authorMention(text, event),
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <EventRow
                key={event.id}
                icon={iconOf(event.type)}
                label={label}
                details={details}
                footnote={footnote}
              />
            );
          })}
        </MonthGroup>
      ))}
      {isMigrated && (
        <ul>
          <EventRow
            icon={{ Icon: ClockIcon, color: '#4B5563', box: 'bg-gray-100' }}
            label={text(
              'migrated',
              'Reprise de l’ancien import : historique antérieur indisponible',
            )}
          />
        </ul>
      )}
    </div>
  );
};

export default InvoiceHistoryTab;
